import type { ReviewHiddenReason, ReviewVisibility } from '../types/review';

export type StarFillState = 'full' | 'empty';

const REVIEW_TEXT_MAX_LENGTH = 1000;
const MOSTLY_DIGITS_RE = /^[\d\s()+\-./٠-٩۰-۹]+$/;

export function normalizeReviewText(value: string | null | undefined): string | null {
  if (value == null || value === '') return null;
  const trimmed = value.trim();
  if (trimmed.length === 0) {
    throw new Error('reviews.errors.whitespaceOnly');
  }
  if (trimmed.length > REVIEW_TEXT_MAX_LENGTH) {
    throw new Error('reviews.errors.textTooLong');
  }
  return trimmed;
}

export function isPhoneLikeDisplayName(value: string): boolean {
  const trimmed = value.trim();
  if (!trimmed) return true;
  if (/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(trimmed)) return true;
  const asciiDigits = trimmed
    .replace(/[٠-٩]/g, (digit) => String('٠١٢٣٤٥٦٧٨٩'.indexOf(digit)))
    .replace(/[۰-۹]/g, (digit) => String('۰۱۲۳۴۵۶۷۸۹'.indexOf(digit)))
    .replace(/\D/g, '');
  return asciiDigits.length >= 7 && MOSTLY_DIGITS_RE.test(trimmed);
}

export function safeReviewDisplayName(value: string | null | undefined): string | null {
  const trimmed = value?.trim() ?? '';
  if (!trimmed || trimmed.length > 100 || isPhoneLikeDisplayName(trimmed)) return null;
  return trimmed;
}

export function formatRating(value: number | null | undefined): number {
  if (typeof value !== 'number' || Number.isNaN(value)) return 0;
  return Math.round(Math.min(Math.max(value, 0), 5) * 10) / 10;
}

export function buildStarFillStates(rating: number): readonly StarFillState[] {
  const rounded = Math.round(Math.min(Math.max(rating, 0), 5));
  return Array.from({ length: 5 }, (_, index) => (index < rounded ? 'full' : 'empty'));
}

export function isReviewVisibility(value: string): value is ReviewVisibility {
  return value === 'visible' || value === 'hidden';
}

export function isReviewHiddenReason(value: string): value is ReviewHiddenReason {
  return (
    value === 'abusive' ||
    value === 'private_info' ||
    value === 'off_topic' ||
    value === 'spam' ||
    value === 'other'
  );
}
